import React, { useEffect, useState } from "react";
import { patchUser, getUser } from "../../api";
import { handleApiError } from "../../utils/apiErrorHandler";
import NotificationService from "../../components/alerts/index";
import {
  ProfileAlert,
  ProfileCard,
  ProfileCardDesc,
  ProfileCardHead,
  ProfileCardTitle,
  ProfileField,
  ProfileFields,
  ProfileFooter,
  ProfileGhostBtn,
  ProfileHint,
  ProfileInput,
  ProfileLabel,
  ProfilePrimaryBtn,
} from "./Profile.styles";

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AlertIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const AccountDetailsPanel = ({ onUserUpdated }) => {
  const [id, setId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [affiliateKey, setAffiliateKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const loadFromStorage = () => {
    const raw = localStorage.getItem("user");
    if (!raw) return;
    const user = JSON.parse(raw);
    setId(user.id);
    setName(user.name || "");
    setEmail(user.email || "");
    setAffiliateKey(user.ownAffiliateKey || "");
  };

  useEffect(() => {
    loadFromStorage();
  }, []);

  const resetForm = () => {
    loadFromStorage();
    setSaved(false);
    setError("");
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaved(false);
    setError("");
    setSaving(true);
    try {
      const res = await patchUser(id, { name, email });
      if (res && res.status === 200) {
        NotificationService.success("Account updated!");
        const userRes = await getUser(id);
        if (userRes.status === 200) {
          localStorage.setItem("user", JSON.stringify(userRes.data));
          loadFromStorage();
          onUserUpdated?.(userRes.data);
        }
        setSaved(true);
      } else {
        const errorResponse = handleApiError(res, "Failed to update account");
        NotificationService.error(errorResponse.message);
        setError(errorResponse.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileCard as="form" onSubmit={onSave}>
      <ProfileCardHead>
        <div>
          <ProfileCardTitle>Account details</ProfileCardTitle>
          <ProfileCardDesc>
            Update the name and email associated with your membership.
          </ProfileCardDesc>
        </div>
      </ProfileCardHead>

      {error ? (
        <ProfileAlert $variant="error">
          <AlertIcon />
          {error}
        </ProfileAlert>
      ) : null}
      {saved ? (
        <ProfileAlert>
          <CheckIcon />
          Your changes have been saved.
        </ProfileAlert>
      ) : null}

      <ProfileFields>
        <ProfileField>
          <ProfileLabel htmlFor="profile-display-name">Display name</ProfileLabel>
          <ProfileInput
            id="profile-display-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
            placeholder="Your name"
            autoComplete="name"
          />
        </ProfileField>

        <ProfileField>
          <ProfileLabel htmlFor="profile-email">Email</ProfileLabel>
          <ProfileInput
            id="profile-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSaved(false);
            }}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </ProfileField>

        <ProfileField>
          <ProfileLabel htmlFor="profile-affiliate">Affiliate ID</ProfileLabel>
          <ProfileInput
            id="profile-affiliate"
            value={affiliateKey}
            disabled
            onChange={() => {}}
          />
          <ProfileHint>
            Your unique affiliate identifier. It can&apos;t be changed — share
            it to earn referral credit.
          </ProfileHint>
        </ProfileField>
      </ProfileFields>

      <ProfileFooter>
        <ProfileGhostBtn type="button" onClick={resetForm} disabled={saving}>
          Cancel
        </ProfileGhostBtn>
        <ProfilePrimaryBtn type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </ProfilePrimaryBtn>
      </ProfileFooter>
    </ProfileCard>
  );
};

export default AccountDetailsPanel;
